import { useMemo, useState } from "react";
import { Check, ChevronDown, X } from "lucide-react";
import { useRoles } from "@/hooks/useRoles";
import { RoleChip } from "@/components/shared/RoleChip";
import { cn } from "@/design-system/lib/utils";

interface Props {
  value: string[];
  onChange: (next: string[]) => void;
  /** Optional id so a <Label htmlFor=> can target the trigger button. */
  id?: string;
  invalid?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

/**
 * Certifications Module v1.0 §4a — multi-select role-code picker. Backed by
 * <see cref="useRoles"/> so it always reflects the canonical
 * <c>/api/roles</c> active list (admin renames propagate within seconds).
 *
 * Renders as a row of <see cref="RoleChip"/> badges plus a popover checkbox
 * list — matches the role-card UX on the Users page so admins build muscle
 * memory across the two surfaces.
 */
export function RolesMultiSelect({
  value,
  onChange,
  id,
  invalid,
  disabled,
  placeholder = "Pick one or more roles",
}: Props) {
  const { data: roles, isLoading } = useRoles();
  const [open, setOpen] = useState(false);

  const sorted = useMemo(
    () => (roles ?? []).slice().sort((a, b) => a.code.localeCompare(b.code)),
    [roles],
  );

  const toggle = (code: string) => {
    if (value.includes(code)) {
      onChange(value.filter((c) => c !== code));
    } else {
      onChange([...value, code]);
    }
  };

  const remove = (code: string) => onChange(value.filter((c) => c !== code));

  return (
    <div className="relative">
      <button
        id={id}
        type="button"
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        className={cn(
          "w-full min-h-10 px-space-3 py-space-2 flex items-center justify-between gap-space-2",
          "font-body text-body text-onyx bg-white-pure",
          "border border-border-light rounded-md text-left",
          "focus:outline-none focus:border-gold focus:shadow-goldRing",
          "disabled:bg-white-warm disabled:opacity-60 disabled:cursor-not-allowed",
          invalid && "border-status-error focus:border-status-error",
        )}
      >
        <div className="flex flex-wrap gap-space-1 flex-1 min-w-0">
          {value.length === 0 ? (
            <span className="text-gray-mid">{isLoading ? "Loading roles…" : placeholder}</span>
          ) : (
            value.map((code) => (
              <span key={code} className="inline-flex items-center">
                <RoleChip code={code} />
                {!disabled && (
                  <button
                    type="button"
                    aria-label={`Remove ${code}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(code);
                    }}
                    className="ml-[2px] p-[1px] rounded hover:bg-white-warm"
                  >
                    <X size={12} aria-hidden />
                  </button>
                )}
              </span>
            ))
          )}
        </div>
        <ChevronDown size={16} aria-hidden className="shrink-0 text-gray-mid" />
      </button>
      {open && !disabled && (
        <>
          <button
            type="button"
            aria-hidden
            tabIndex={-1}
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
          />
          <div
            role="listbox"
            aria-multiselectable
            className={cn(
              "absolute z-20 mt-space-1 left-0 right-0 max-h-64 overflow-y-auto",
              "bg-white-pure border border-border-light rounded-md shadow-lg",
            )}
          >
            {sorted.length === 0 && (
              <div className="px-space-3 py-space-2 text-small text-gray-mid">
                No roles available.
              </div>
            )}
            {sorted.map((r) => {
              const selected = value.includes(r.code);
              return (
                <button
                  key={r.code}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => toggle(r.code)}
                  className={cn(
                    "w-full text-left px-space-3 py-space-2 flex items-start gap-space-2",
                    "hover:bg-white-warm cursor-pointer",
                    selected && "bg-gold/5",
                  )}
                >
                  <span
                    aria-hidden
                    className={cn(
                      "mt-[2px] h-4 w-4 inline-flex items-center justify-center rounded border",
                      selected ? "border-gold bg-gold text-onyx" : "border-border-light",
                    )}
                  >
                    {selected && <Check size={12} aria-hidden />}
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="font-mono text-small font-semibold text-onyx">{r.code}</span>
                    <span className="block text-caption text-gray-mid leading-tight truncate">
                      {r.displayName}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
